import {
  ApiError,
  type AlertsReport,
  type CategoryReportRow,
  type EmployeeReportRow,
  type ListReport,
  type ProductReportRow,
  type ShiftReportRow,
  type SummaryReport,
} from '@pos/api-client';
import { formatAmount, formatQuantity } from '@pos/business-logic';
import type { ProductUnit } from '@pos/types';
import {
  EmptyState,
  ErrorState,
  ListGroup,
  ListRow,
  Money,
  Segmented,
  Skeleton,
  cn,
} from '@pos/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, useAuth } from '../../app/auth';
import { BranchChip } from '../../app/BranchChip';
import { Screen } from '../../app/Screen';
import { useBranch } from '../../app/data';
import { METHOD_LABEL, when } from '../../lib/format';

type Preset = 'today' | 'week' | 'month';
type View = 'summary' | 'products' | 'staff' | 'shifts' | 'attention';

function useReport<T>(kind: string, preset: Preset) {
  const branchId = useBranch((s) => s.branchId);
  const biz = useAuth((s) => s.businessId);
  return useQuery({
    queryKey: ['report', biz, kind, preset, branchId],
    queryFn: () => api.get<T>(`/reports/${kind}`, { preset, branchId: branchId ?? undefined }),
    staleTime: 60_000,
  });
}

function Loading() {
  return <Skeleton className="h-64 rounded-lg" />;
}

function Summary({ preset }: { preset: Preset }) {
  const q = useReport<SummaryReport>('summary', preset);
  const d = q.data;
  if (q.isError)
    return (
      <ErrorState description="Не удалось построить отчёт." onRetry={() => void q.refetch()} />
    );
  if (!d) return <Loading />;
  const t = d.totals;
  const peak = Math.max(1, ...d.byDay.map((x) => x.revenue));
  return (
    <>
      <div className="mb-3 rounded-2xl border border-line bg-surface p-4">
        <p className="text-sm text-ink-3">Выручка</p>
        <Money value={t.revenue} className="mt-1 block text-4xl font-bold tracking-[-0.02em]" />
        <p className="mt-1 text-sm text-ink-3 tnum">
          {t.count} чеков · средний {formatAmount(t.average)}
          {t.refunds ? ` · возвраты −${formatAmount(t.refunds)}` : ''}
        </p>
      </div>
      <ListGroup title="Деньги">
        <ListRow
          title="Себестоимость проданного"
          trailing={<span className="tnum">{formatAmount(t.cogs)}</span>}
        />
        <ListRow
          title="Валовая прибыль"
          trailing={<span className="font-medium tnum">{formatAmount(t.grossProfit)}</span>}
        />
        <ListRow
          title="Расходы"
          trailing={
            <span className="tnum">{t.expenses ? `−${formatAmount(t.expenses)}` : '0'}</span>
          }
        />
        <ListRow
          title="Скидки покупателям"
          trailing={<span className="tnum">{formatAmount(t.discounts)}</span>}
        />
        <ListRow
          title={<span className="font-semibold">Прибыль, оценка</span>}
          trailing={
            <span className={cn('font-semibold tnum', t.profit < 0 && 'text-danger')}>
              {formatAmount(t.profit)}
            </span>
          }
        />
      </ListGroup>
      {d.byDay.length > 1 && (
        <ListGroup title="По дням">
          {d.byDay.map((x) => (
            <div
              key={x.date}
              className="flex min-h-12 items-center gap-3 border-b border-line px-4 last:border-b-0"
            >
              <span className="w-16 shrink-0 text-sm text-ink-3">
                {new Date(`${x.date}T12:00:00`).toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'short',
                })}
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-rail">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${Math.max(2, (x.revenue / peak) * 100)}%` }}
                />
              </div>
              <span className="w-24 shrink-0 text-right text-sm font-medium tnum">
                {formatAmount(x.revenue)}
              </span>
            </div>
          ))}
        </ListGroup>
      )}
      {d.payments.length > 0 && (
        <ListGroup title="Оплата">
          {d.payments.map((p) => (
            <ListRow
              key={p.method}
              title={METHOD_LABEL[p.method]}
              trailing={<span className="tnum">{formatAmount(p.amount)}</span>}
            />
          ))}
        </ListGroup>
      )}
    </>
  );
}

function Products({ preset }: { preset: Preset }) {
  const products = useReport<ListReport<ProductReportRow>>('products', preset);
  const categories = useReport<ListReport<CategoryReportRow>>('categories', preset);
  const navigate = useNavigate();
  if (products.isError)
    return (
      <ErrorState
        description="Не удалось построить отчёт."
        onRetry={() => void products.refetch()}
      />
    );
  if (!products.data) return <Loading />;
  if (products.data.items.length === 0)
    return <EmptyState icon={<BarChart3 />} title="Продаж за период нет" />;
  return (
    <>
      <ListGroup title="Лидеры по выручке">
        {products.data.items.slice(0, 15).map((p, i) => (
          <ListRow
            key={`${p.productId ?? 'm'}:${p.name}`}
            leading={
              <span className="w-5 text-center text-sm font-semibold text-ink-3 tnum">{i + 1}</span>
            }
            title={p.name}
            subtitle={`${formatQuantity(p.quantity, p.unit as ProductUnit)} · маржа ${p.margin}%`}
            trailing={<span className="font-medium tnum">{formatAmount(p.revenue)}</span>}
            onClick={p.productId ? () => navigate(`/products/${p.productId}`) : undefined}
          />
        ))}
      </ListGroup>
      {categories.data && categories.data.items.length > 0 && (
        <ListGroup title="Категории">
          {categories.data.items.map((c) => (
            <div
              key={c.categoryId ?? c.name}
              className="border-b border-line px-4 py-3 last:border-b-0"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-md">{c.name}</span>
                <span className="text-sm font-medium tnum">{formatAmount(c.revenue)}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-rail">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${c.share}%` }}
                  />
                </div>
                <span className="w-12 text-right text-xs text-ink-3 tnum">{c.share}%</span>
              </div>
            </div>
          ))}
        </ListGroup>
      )}
    </>
  );
}

function Staff({ preset }: { preset: Preset }) {
  const q = useReport<ListReport<EmployeeReportRow>>('employees', preset);
  if (q.isError)
    return (
      <ErrorState description="Не удалось построить отчёт." onRetry={() => void q.refetch()} />
    );
  if (!q.data) return <Loading />;
  if (q.data.items.length === 0)
    return <EmptyState icon={<BarChart3 />} title="За период никто не продавал" />;
  return (
    <ListGroup>
      {q.data.items.map((e) => (
        <ListRow
          key={e.employeeId}
          title={e.name}
          subtitle={[
            `${e.receipts} чеков · средний ${formatAmount(e.average)}`,
            e.returns ? `возвратов ${e.returns}` : null,
            e.cashDifference
              ? `расхождение ${e.cashDifference > 0 ? '+' : ''}${formatAmount(e.cashDifference)}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          trailing={<span className="font-medium tnum">{formatAmount(e.revenue)}</span>}
        />
      ))}
    </ListGroup>
  );
}

/**
 * Every shift of the period: who, which till, what the drawer should have
 * held against what was counted, and how much was removed from checks.
 */
function Shifts({ preset }: { preset: Preset }) {
  const q = useReport<
    ListReport<ShiftReportRow> & {
      count: number;
      totals: {
        sales: number;
        difference: number;
        removedLines: number;
        removedAmount: number;
        clearedChecks: number;
      };
    }
  >('shifts', preset);
  if (q.isError) {
    const locked = q.error instanceof ApiError && q.error.status === 402;
    return locked ? (
      <EmptyState
        icon={<BarChart3 />}
        title="Отчёт по сменам — на тарифе с расширенными отчётами"
        description="Смените тариф в разделе «Подписка»."
      />
    ) : (
      <ErrorState description="Не удалось построить отчёт." onRetry={() => void q.refetch()} />
    );
  }
  if (!q.data) return <Loading />;
  if (q.data.items.length === 0)
    return <EmptyState icon={<BarChart3 />} title="Смен за период не было" />;
  const t = q.data.totals;
  return (
    <>
      <div className="mb-3 grid grid-cols-3 gap-2">
        <div className="rounded-2xl border border-line bg-surface p-3">
          <p className="text-xs text-ink-3">Смен</p>
          <p className="text-xl font-bold tnum">{q.data.count}</p>
        </div>
        <div className="rounded-2xl border border-line bg-surface p-3">
          <p className="text-xs text-ink-3">Расхождение</p>
          <p
            className={cn(
              'text-xl font-bold tnum',
              t.difference < 0 ? 'text-danger' : t.difference > 0 ? 'text-warning' : undefined,
            )}
          >
            {t.difference > 0 ? '+' : ''}
            {formatAmount(t.difference)}
          </p>
        </div>
        <div className="rounded-2xl border border-line bg-surface p-3">
          <p className="text-xs text-ink-3">Удалений</p>
          <p className="text-xl font-bold tnum">{t.removedLines + t.clearedChecks}</p>
        </div>
      </div>
      <ListGroup>
        {q.data.items.map((s) => {
          const diff = s.difference ?? 0;
          const removals = s.removedLines + s.clearedChecks;
          return (
            <ListRow
              key={s.id}
              title={`Смена №${s.number} · касса №${s.registerNumber}`}
              subtitle={
                <span>
                  {[
                    s.openedBy,
                    when(s.openedAt),
                    s.status === 'OPEN'
                      ? 'открыта'
                      : s.closedAt
                        ? `закрыта ${when(s.closedAt)}`
                        : null,
                    s.branchName,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  {s.status === 'CLOSED' && diff !== 0 && (
                    <span className="block text-danger">
                      {diff < 0 ? 'Недостача' : 'Излишек'} {formatAmount(Math.abs(diff))}
                    </span>
                  )}
                  {removals > 0 && (
                    <span className={cn('block', removals >= 10 ? 'text-warning' : 'text-ink-3')}>
                      Удалено позиций {s.removedLines} на {formatAmount(s.removedAmount)}
                      {s.clearedChecks ? `, очищено чеков ${s.clearedChecks}` : ''}
                    </span>
                  )}
                </span>
              }
              trailing={
                <span className="text-right">
                  <span className="block font-medium tnum">{formatAmount(s.sales)}</span>
                  {s.status === 'OPEN' && <span className="block text-xs text-success">идёт</span>}
                </span>
              }
            />
          );
        })}
      </ListGroup>
    </>
  );
}

function Attention({ preset }: { preset: Preset }) {
  const q = useReport<AlertsReport>('alerts', preset);
  const navigate = useNavigate();
  if (q.isError)
    return (
      <ErrorState description="Не удалось построить отчёт." onRetry={() => void q.refetch()} />
    );
  const d = q.data;
  if (!d) return <Loading />;
  const nothing =
    d.alerts.length === 0 && d.unusualSales.length === 0 && d.cashDifferences.length === 0;
  if (nothing)
    return (
      <EmptyState
        icon={<BarChart3 />}
        title="Всё спокойно"
        description="Нет расхождений, необычных чеков и касс без связи."
      />
    );
  return (
    <>
      {d.alerts.length > 0 && (
        <ul className="mb-6 space-y-2">
          {d.alerts.map((a, i) => (
            <li
              key={i}
              className={cn(
                'flex items-start gap-3 rounded-lg border px-4 py-3',
                a.severity === 'danger'
                  ? 'border-danger-line bg-danger-soft text-danger'
                  : 'border-warning-line bg-warning-soft text-warning',
              )}
            >
              <AlertTriangle className="mt-0.5 size-5 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium">{a.title}</p>
                {a.detail && a.kind !== 'DEVICE_STALE' && (
                  <p className="text-sm opacity-80">{a.detail}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {d.cashDifferences.length > 0 && (
        <ListGroup title="Расхождения в кассе">
          {d.cashDifferences.map((c) => (
            <ListRow
              key={c.shiftId}
              title={`Смена №${c.shiftNumber} · касса №${c.registerNumber}`}
              subtitle={[c.employeeName, c.closedAt && when(c.closedAt)]
                .filter(Boolean)
                .join(' · ')}
              trailing={
                <span
                  className={cn(
                    'font-semibold tnum',
                    c.difference < 0 ? 'text-danger' : 'text-warning',
                  )}
                >
                  {c.difference > 0 ? '+' : ''}
                  {formatAmount(c.difference)}
                </span>
              }
            />
          ))}
        </ListGroup>
      )}
      {d.unusualSales.length > 0 && (
        <ListGroup title="Необычные чеки">
          {d.unusualSales.map((u) => (
            <ListRow
              key={u.saleId}
              title={`${u.number} · ${u.employeeName}`}
              subtitle={<span className="text-warning">{u.reason}</span>}
              trailing={<span className="tnum">{formatAmount(u.total)}</span>}
              onClick={() => navigate(`/sales/${u.saleId}`)}
            />
          ))}
        </ListGroup>
      )}
    </>
  );
}

export function ReportsPage() {
  const [preset, setPreset] = useState<Preset>('week');
  const [view, setView] = useState<View>('summary');
  return (
    <Screen title="Отчёты" back="/more">
      <div className="space-y-3">
        <Segmented
          label="Период"
          value={preset}
          onChange={setPreset}
          options={[
            { value: 'today', label: 'Сегодня' },
            { value: 'week', label: 'Неделя' },
            { value: 'month', label: 'Месяц' },
          ]}
          className="w-full [&>button]:flex-1"
        />
        <div
          className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1"
          role="tablist"
          aria-label="Отчёт"
        >
          {(
            [
              ['summary', 'Сводка'],
              ['products', 'Товары'],
              ['staff', 'Персонал'],
              ['shifts', 'Смены'],
              ['attention', 'Внимание'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              onClick={() => setView(v)}
              className={cn(
                'h-11 shrink-0 rounded-md border px-3.5 text-base',
                view === v
                  ? 'border-ink bg-ink font-medium text-white'
                  : 'border-line-strong bg-surface active:bg-sunken',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <BranchChip />
        </div>
        <div className="pt-1">
          {view === 'summary' && <Summary preset={preset} />}
          {view === 'products' && <Products preset={preset} />}
          {view === 'staff' && <Staff preset={preset} />}
          {view === 'shifts' && <Shifts preset={preset} />}
          {view === 'attention' && <Attention preset={preset} />}
        </div>
      </div>
    </Screen>
  );
}
